import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import MediaPicker from "@/components/admin/MediaPicker";
import AiAssistButton from "@/components/admin/AiAssistButton";
import { ATID } from "@/lib/adminTestIds";
import { Search, Share2, Image as ImageIcon, X, Globe, Facebook, Twitter } from "lucide-react";

/**
 * Reusable SEO editor block used inside article and category editors.
 * Props:
 *   - form: object with { seo_title, seo_description, seo_keywords, og_image, canonical_url, slug }
 *   - setField: (patch) => void
 *   - currentTitle: current title of the resource
 *   - currentSummary: current summary
 *   - currentMainImage: current main image URL
 *   - resourceType: "article" | "category"
 *   - aiPayload: optional () => ({title, summary, content, tags, category})
 *      Enables an "Generar con IA" button that fills title/description/keywords.
 */
export default function SEOSection({
  form,
  setField,
  currentTitle = "",
  currentSummary = "",
  currentMainImage = "",
  resourceType = "article",
  aiPayload = null,
}) {
  const [mediaOpen, setMediaOpen] = useState(false);

  const effective = useMemo(() => {
    const title = (form.seo_title || currentTitle || "").slice(0, 70);
    const description = (form.seo_description || currentSummary || "").slice(0, 200);
    const image = form.og_image || currentMainImage;
    const url = form.canonical_url ||
      (typeof window !== "undefined"
        ? `${window.location.origin}/${resourceType === "category" ? "categoria" : "noticia"}/${form.slug || "slug-de-ejemplo"}`
        : "");
    return { title, description, image, url };
  }, [form.seo_title, form.seo_description, form.og_image, form.canonical_url, form.slug, currentTitle, currentSummary, currentMainImage, resourceType]);

  const titleLen = (form.seo_title || currentTitle || "").length;
  const descLen = (form.seo_description || currentSummary || "").length;

  return (
    <div data-testid={ATID.seoSection} className="bg-white border border-softblue p-5 mt-2">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-turquoise" />
          <h3 className="font-serif text-lg font-bold text-blueblack">SEO y compartir</h3>
        </div>
        {aiPayload && (
          <AiAssistButton
            task="seo"
            label="Generar con IA"
            testId={ATID.aiSeo}
            getPayload={aiPayload}
            onResult={(r) => {
              const patch = {};
              if (r?.seo_title) patch.seo_title = r.seo_title;
              if (r?.seo_description) patch.seo_description = r.seo_description;
              if (Array.isArray(r?.seo_keywords) && r.seo_keywords.length)
                patch.seo_keywords = r.seo_keywords.join(", ");
              if (Object.keys(patch).length) setField(patch);
            }}
          />
        )}
      </div>
      <p className="text-xs text-textgray mb-4">
        Estos campos controlan cómo aparece {resourceType === "category" ? "la categoría" : "la noticia"} en buscadores y redes sociales.
        Si los dejás vacíos se usan el título, resumen y foto principal.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Título SEO</Label>
              <span className={`text-[10px] ${titleLen > 60 ? "text-amber-600" : "text-textgray"}`}>{titleLen}/60</span>
            </div>
            <Input
              data-testid={ATID.seoTitle}
              value={form.seo_title || ""}
              onChange={(e) => setField({ seo_title: e.target.value })}
              placeholder={currentTitle || "Título para Google (recomendado 50-60 caracteres)"}
              className="h-10 bg-white border-softblue"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción SEO</Label>
              <span className={`text-[10px] ${descLen > 160 ? "text-amber-600" : "text-textgray"}`}>{descLen}/160</span>
            </div>
            <Textarea
              data-testid={ATID.seoDescription}
              value={form.seo_description || ""}
              onChange={(e) => setField({ seo_description: e.target.value })}
              placeholder={currentSummary || "Descripción de máx. 160 caracteres"}
              rows={3}
              className="bg-white border-softblue resize-none"
            />
          </div>

          {resourceType === "article" && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Palabras clave</Label>
              <Input
                data-testid={ATID.seoKeywords}
                value={form.seo_keywords || ""}
                onChange={(e) => setField({ seo_keywords: e.target.value })}
                placeholder="separadas, por, coma"
                className="mt-2 h-10 bg-white border-softblue"
              />
              <div className="text-[10px] text-textgray mt-1">
                Si lo dejás vacío, se usan las etiquetas de la noticia.
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold flex items-center gap-1">
              <Globe className="w-3.5 h-3.5" /> URL canonical
            </Label>
            <Input
              data-testid={ATID.seoCanonical}
              value={form.canonical_url || ""}
              onChange={(e) => setField({ canonical_url: e.target.value })}
              placeholder="https://surprimicia.com.ar/..."
              className="mt-2 h-10 bg-white border-softblue"
            />
            <div className="text-[10px] text-textgray mt-1">
              Opcional. Usá esto si la nota fue publicada primero en otro sitio.
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5" /> Imagen para redes (Open Graph)
            </Label>
            <div className="mt-2 flex gap-2">
              {form.og_image ? (
                <div className="relative">
                  <img
                    src={form.og_image}
                    alt="OG"
                    className="w-32 h-20 object-cover border border-softblue"
                  />
                  <button
                    type="button"
                    data-testid={ATID.seoOgImageRemove}
                    onClick={() => setField({ og_image: "" })}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-softblue text-red-500 hover:bg-red-50 flex items-center justify-center"
                    aria-label="Quitar imagen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-20 bg-lightgray border border-dashed border-softblue text-textgray text-[10px] flex items-center justify-center">
                  Sin imagen
                </div>
              )}
              <Button
                type="button"
                data-testid={ATID.seoOgImagePick}
                variant="outline"
                onClick={() => setMediaOpen(true)}
                className="h-10 border-softblue"
              >
                <ImageIcon className="w-4 h-4 mr-2" /> Elegir de la biblioteca
              </Button>
            </div>
            <div className="text-[10px] text-textgray mt-1">
              Recomendado: 1200×630px. Si lo dejás vacío se usa la imagen principal.
            </div>
          </div>
        </div>

        {/* Preview column */}
        <div className="space-y-4">
          <Label className="text-xs uppercase tracking-widest text-textgray font-semibold flex items-center gap-1">
            <Share2 className="w-3.5 h-3.5" /> Vista previa
          </Label>

          {/* Google preview */}
          <div data-testid={ATID.seoPreviewGoogle} className="border border-softblue rounded-md p-4 bg-lightgray/40">
            <div className="text-[10px] uppercase tracking-widest text-textgray mb-2 flex items-center gap-1">
              <Search className="w-3 h-3" /> Google
            </div>
            <div className="text-xs text-emerald-700 truncate">{effective.url}</div>
            <div className="text-blue-800 text-base font-medium leading-snug mt-1 line-clamp-2">
              {effective.title || "Título de la noticia"}
            </div>
            <div className="text-textgray text-xs mt-1 line-clamp-2">
              {effective.description || "Descripción para mostrar en los resultados de búsqueda."}
            </div>
          </div>

          {/* Facebook / WhatsApp preview */}
          <div data-testid={ATID.seoPreviewFb} className="border border-softblue rounded-md overflow-hidden bg-white">
            <div className="text-[10px] uppercase tracking-widest text-textgray p-2 border-b border-softblue flex items-center gap-1">
              <Facebook className="w-3 h-3" /> Facebook / WhatsApp
            </div>
            {effective.image ? (
              <img src={effective.image} alt="OG preview" className="w-full aspect-[1200/630] object-cover" />
            ) : (
              <div className="w-full aspect-[1200/630] bg-lightgray text-textgray text-xs flex items-center justify-center">
                Sin imagen 1200×630
              </div>
            )}
            <div className="p-3 bg-lightgray/40">
              <div className="text-[10px] uppercase tracking-widest text-textgray truncate">
                {(() => {
                  try {
                    return new URL(effective.url).hostname;
                  } catch { return "surprimicia.com.ar"; }
                })()}
              </div>
              <div className="text-sm font-semibold text-nightblue mt-0.5 line-clamp-2">
                {effective.title || "Título de la noticia"}
              </div>
              <div className="text-textgray text-xs mt-1 line-clamp-2">
                {effective.description}
              </div>
            </div>
          </div>

          {/* Twitter preview */}
          <div data-testid={ATID.seoPreviewTwitter} className="border border-softblue rounded-md overflow-hidden bg-white">
            <div className="text-[10px] uppercase tracking-widest text-textgray p-2 border-b border-softblue flex items-center gap-1">
              <Twitter className="w-3 h-3" /> X / Twitter
            </div>
            {effective.image ? (
              <img src={effective.image} alt="Twitter preview" className="w-full aspect-[2/1] object-cover" />
            ) : (
              <div className="w-full aspect-[2/1] bg-lightgray text-textgray text-xs flex items-center justify-center">
                Sin imagen (summary_large_image)
              </div>
            )}
            <div className="p-3">
              <div className="text-sm font-semibold text-nightblue line-clamp-2">
                {effective.title || "Título de la noticia"}
              </div>
              <div className="text-textgray text-xs mt-1 line-clamp-2">
                {effective.description}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-textgray mt-1">
                {(() => {
                  try { return new URL(effective.url).hostname; } catch { return "surprimicia.com.ar"; }
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <MediaPicker
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(media) => {
          setField({ og_image: media?.variants?.social?.url || media?.url });
          setMediaOpen(false);
        }}
      />
    </div>
  );
}
